/**
 * vila.js - VILA ALTA, nucleo antigo dentro da muralha.
 *
 * IDENTIDADE: o mapa e feito de RUA ESTREITA e CASA BAIXA. Nao ha patio aberto
 * (porto) nem vao unico coberto (deposito): ha um tabuleiro de nove quarteiroes
 * de casas de dois pavimentos, separados por ruas de 3,6 m, cortados por becos
 * de 1,8 m e amarrados por uma rua da muralha que da a volta no mapa inteiro.
 * O combate e de ESQUINA embaixo e de TELHADO em cima - nunca de campo aberto.
 *
 * PLANTA (visto de cima, +X leste, +Z norte; medidas em metro)
 *
 *   z=+34  ##################### MURALHA (5,0 m) #####################
 *   z=+30  |  rua da muralha 3,6                                     |
 *          | QUARTEIRAO NO | RUA | QUARTEIRAO NC | RUA | QUARTEIRAO NE |
 *   z=+22  |  ---- beco ---|  W  |     beco      |  E  | ---- beco ----|
 *   z=+13  |=============== TRAVESSA NORTE 3,6 ======================|
 *   z=+9   | QUARTEIRAO O  |     |    P R A C A  |     | QUARTEIRAO L |
 *   z=+0   |  ---- beco ---|     | fonte, feira, |     | ---- beco ---|
 *   z=-9   |               |     | arcada, torre |     |              |
 *   z=-13  |=============== TRAVESSA SUL 3,6 ========================|
 *   z=-22  | QUARTEIRAO SO | beco| QUARTEIRAO SC | beco| QUARTEIRAO SE|
 *   z=-30  |  rua da muralha                                          |
 *   z=-34  ###########################################################
 *
 * VERTICALIDADE: nove escadas externas saem da rua (ou da praca) para os
 * terracos de 3,6 m e para os telhados de 6,4 m; quatro lances curtos ligam
 * terraco a telhado; oito pranchas e passarelas atravessam becos e ruas la em
 * cima, mais uma rampa de entulho no beco sudeste. O telhado NAO e um tabuleiro
 * unico: sao ilhas regionais, e trocar de ilha custa descer.
 *
 * COBERTURA: cada rua leva MURO DE QUINTAL atravessado, com um vao encostado
 * numa das laterais; o muro seguinte da mesma rua poe o vao na outra.
 * MEDIDO com raio contra a malha de colisao, na altura do olho: o maior vao
 * reto de rua e de 49,4 m (travessa sul) e o da rua da muralha caiu para 25,9 m
 * - nenhuma reta paralela a rua atravessa os 68 m. O eixo que junta beco sul,
 * travessa, praca e beco norte era a excecao: media 67,8 m de ponta a ponta, e
 * quem o fecha e o BLOCO do pedestal do monumento (2,0 m de largura por 2,15 m
 * de altura, acima da linha do olho) somado a dois muros de beco com o vao em
 * lados opostos. Depois deles: 36,1 m.
 * No nivel dos telhados o corte vem da torre (11,6 m), de 22 caixas de escada
 * de 2,2 a 2,4 m e do desnivel 3,6 / 6,4. A platibanda de 1,0 m cobre quem esta
 * agachado, mas passa por baixo do olho de quem esta em pe - la em cima o mapa
 * se comporta como o topo das pilhas do porto, e e para isso que servem as
 * barricadas de saco de areia.
 *
 * PERIMETRO: muralha de 5,0 m fechada nos quatro lados, com quatro torres de
 * canto. Nao ha borda por onde cair.
 */

import * as P from '../Props.js';

// ---------------------------------------------------------------------------
// coordenadas
// ---------------------------------------------------------------------------

const MINX = -34, MAXX = 34, MINZ = -34, MAXZ = 34;
const WALL = 1.4;              // espessura da muralha
const H_WALL = 5.0;            // altura da muralha

const Y_ST = 0.05;             // rua de terra batida
const Y_STEP = 0.19;           // degrau que circunda a praca
const Y_SQ = 0.34;             // piso da praca
const Y_IN = 0.14;             // piso dos interiores abertos

const RING = 30.4;             // face externa dos quarteiroes (rua da muralha)
const AV_W0 = -13.2, AV_W1 = -9.6;   // rua norte-sul oeste
const AV_E0 = 9.6, AV_E1 = 13.2;     // rua norte-sul leste
const TV_S0 = -13.2, TV_S1 = -9.6;   // travessa leste-oeste sul
const TV_N0 = 9.6, TV_N1 = 13.2;     // travessa leste-oeste norte

const H_CASA = 6.4;            // casa de dois pavimentos (telhado caminhavel)
const H_TERR = 3.6;            // anexo de um pavimento (terraco intermediario)

/** Praca: SQ_OUT e a borda do quarteirao central, SQ o fim do piso de pedra. */
const SQ_OUT = 9.6;
const SQ = 8.9;

/** Becos que cortam os quarteiroes: [x0, z0, x1, z1]. */
const BECOS = [
  [-22.8, -RING, -21.0, TV_S0],   // sudoeste (norte-sul)
  [21.0, -RING, 22.8, TV_S0],     // sudeste (norte-sul)
  [-1.2, -RING, 0.6, TV_S0],      // sul (norte-sul)
  [-RING, -1.2, AV_W0, 0.6],      // oeste (leste-oeste)
  [AV_E1, -0.6, RING, 1.2],       // leste (leste-oeste)
  [-RING, 20.4, AV_W0, 22.2],     // noroeste (leste-oeste)
  [AV_E1, 20.4, RING, 22.2],      // nordeste (leste-oeste)
  [-0.6, TV_N1, 1.2, RING]        // norte (norte-sul)
];

/** Patio murado do quarteirao sul: e por ele que se sobe ao terraco da D2. */
const PAT_X0 = -3.4, PAT_X1 = -1.2, PAT_Z0 = -22.2, PAT_Z1 = TV_S0;

/** Arcada (alpendre) na borda norte da praca. */
const ARC_X0 = -8.2, ARC_X1 = 3.2, ARC_Z0 = 6.2, ARC_Z1 = 9.6;
const ARC_Y = 3.4, ARC_TOP = 3.7;

/** Torre do relogio: o marco da vila e o unico bloqueio alto sobre a praca. */
const TWR_X0 = 5.4, TWR_X1 = 8.6, TWR_Z0 = 4.8, TWR_Z1 = 8.0, TWR_H = 11.6;

/**
 * Esquadria padrao da vila: vao pequeno e alto. Casa de nucleo antigo tem pouca
 * abertura - janela grande e de predio, e predio aqui descaracterizaria o mapa.
 */
const WIN = {
  wallT: 0.36, winW: 0.95, winH: 1.25, winSpacing: 3.5, winMargin: 1.25, sill: 0.95
};

export const meta = {
  id: 'vila',
  name: 'VILA',
  sub: 'VILA ALTA - N\u00daCLEO ANTIGO',
  bounds: { minX: MINX, maxX: MAXX, minZ: MINZ, maxZ: MAXZ },
  ambiente: 'tarde'
};

/**
 * As 34 casas. `f` sao as fachadas DETALHADAS (janela, porta, remendo de tijolo,
 * encardido): so entram as que dao para rua, beco, patio ou praca - fachada
 * cega contra a casa vizinha nao paga o detalhe. `g` sao as falhas da platibanda,
 * e cada uma existe porque uma escada ou uma prancha chega ali.
 */
const HOUSES = [
  // --- quarteirao sudoeste ------------------------------------------------
  { x0: -30.4, z0: -30.4, x1: -22.8, z1: -21.8, h: H_CASA, wall: 'plaster', seed: 301,
    reg: 'vila_sul', f: ['s', 'w', 'e'], clean: true,
    doors: [{ side: 's', u: -27.0, w: 1.2, h: 2.25, style: 'wood', ajar: 0.0 }],
    g: [{ side: 'e', u0: -26.9, u1: -25.1 }, { side: 'n', u0: -27.7, u1: -25.3 }] },
  { x0: -30.4, z0: -21.8, x1: -22.8, z1: TV_S0, h: H_TERR, wall: 'brick', seed: 302,
    reg: 'vila_sul', f: ['w', 'e', 'n'],
    doors: [{ side: 'n', u: -26.0, w: 1.2, h: 2.25, style: 'metal', ajar: 0.0 }],
    g: [{ side: 'w', u0: -16.9, u1: -15.1 }, { side: 's', u0: -27.7, u1: -25.3 }] },
  { x0: -21.0, z0: -30.4, x1: AV_W0, z1: -22.4, h: H_CASA, wall: 'plaster', seed: 303,
    reg: 'vila_sul', f: ['s', 'w', 'e'], clean: true,
    doors: [{ side: 'e', u: -27.6, w: 1.2, h: 2.25, style: 'wood', ajar: 0.0 }],
    g: [{ side: 'w', u0: -26.9, u1: -25.1 }, { side: 'e', u0: -27.3, u1: -24.7 }] },
  { x0: -21.0, z0: -22.4, x1: -14.4, z1: TV_S0, h: H_CASA, wall: 'brick', seed: 304,
    reg: 'vila_sul', f: ['w', 'e', 'n'],
    doors: [{ side: 'n', u: -18.0, w: 1.2, h: 2.25, style: 'wood', ajar: 0.0 }], g: [] },

  // --- quarteirao oeste ---------------------------------------------------
  { x0: -30.4, z0: -9.6, x1: -21.8, z1: -1.2, h: H_CASA, wall: 'plaster', seed: 311,
    reg: 'vila_centro', f: ['s', 'w', 'n'], clean: true,
    doors: [{ side: 's', u: -26.0, w: 1.2, h: 2.25, style: 'wood', ajar: 0.0 }],
    g: [{ side: 'e', u0: -6.5, u1: -4.3 }] },
  { x0: -21.8, z0: -9.6, x1: AV_W0, z1: -1.2, h: H_TERR, wall: 'plaster', seed: 312,
    reg: 'vila_centro', f: ['s', 'e', 'n'],
    doors: [{ side: 's', u: -17.0, w: 1.2, h: 2.25, style: 'metal', ajar: 0.0 }],
    g: [{ side: 'e', u0: -4.9, u1: -3.2 }, { side: 'w', u0: -6.5, u1: -4.3 }] },
  { x0: -30.4, z0: 0.6, x1: -22.6, z1: 9.6, h: H_CASA, wall: 'brick', seed: 313,
    reg: 'vila_centro', f: ['s', 'w', 'n'], clean: true,
    doors: [{ side: 'n', u: -26.0, w: 1.2, h: 2.25, style: 'wood', ajar: 0.0 }], g: [] },
  { x0: -22.6, z0: 0.6, x1: AV_W0, z1: 9.6, h: H_CASA, wall: 'plaster', seed: 314,
    reg: 'vila_centro', f: ['s', 'e', 'n'],
    doors: [{ side: 'e', u: 5.0, w: 1.2, h: 2.25, style: 'wood', ajar: 0.0 }], g: [] },

  // --- quarteirao noroeste ------------------------------------------------
  { x0: -30.4, z0: TV_N1, x1: -21.4, z1: 20.4, h: H_CASA, wall: 'plaster', seed: 321,
    reg: 'vila_norte', f: ['s', 'w', 'n'],
    doors: [{ side: 's', u: -26.0, w: 1.2, h: 2.25, style: 'metal', ajar: 0.0 }],
    g: [{ side: 'n', u0: -26.9, u1: -25.1 }] },
  { x0: -21.4, z0: TV_N1, x1: AV_W0, z1: 20.4, h: H_TERR, wall: 'brick', seed: 322,
    reg: 'vila_norte', f: ['s', 'e', 'n'],
    doors: [{ side: 's', u: -17.5, w: 1.2, h: 2.25, style: 'wood', ajar: 0.0 }], g: [] },
  { x0: -30.4, z0: 22.2, x1: -22.2, z1: RING, h: H_CASA, wall: 'plaster', seed: 323,
    reg: 'vila_norte', f: ['s', 'w', 'n'], clean: true,
    doors: [{ side: 'n', u: -26.0, w: 1.2, h: 2.25, style: 'wood', ajar: 0.0 }],
    g: [{ side: 'w', u0: 28.0, u1: 29.5 }, { side: 's', u0: -26.9, u1: -25.1 }] },
  { x0: -22.2, z0: 22.2, x1: AV_W0, z1: RING, h: H_CASA, wall: 'brick', seed: 324,
    reg: 'vila_norte', f: ['s', 'e', 'n'], clean: true,
    doors: [{ side: 'n', u: -18.0, w: 1.2, h: 2.25, style: 'metal', ajar: 0.0 }], g: [] },

  // --- quarteirao sul (centro) --------------------------------------------
  { x0: AV_W1, z0: -30.4, x1: -1.2, z1: -22.2, h: H_CASA, wall: 'plaster', seed: 331,
    reg: 'vila_sul', f: ['s', 'w', 'e', 'n'],
    doors: [{ side: 's', u: -5.4, w: 1.2, h: 2.25, style: 'wood', ajar: 0.0 }],
    g: [{ side: 'w', u0: -27.3, u1: -24.7 }] },
  { x0: AV_W1, z0: -22.2, x1: PAT_X0, z1: TV_S0, h: H_TERR, wall: 'brick', seed: 332,
    reg: 'vila_sul', f: ['w', 'e', 'n'],
    doors: [{ side: 'n', u: -7.0, w: 1.2, h: 2.25, style: 'wood', ajar: 0.0 }],
    g: [{ side: 'n', u0: -7.4, u1: -5.6 }, { side: 'e', u0: -16.8, u1: -15.2 }] },
  { x0: 0.6, z0: -30.4, x1: AV_E0, z1: -21.0, h: H_CASA, wall: 'brick', seed: 333,
    reg: 'vila_sul', f: ['s', 'w', 'e'], clean: true,
    doors: [{ side: 's', u: 5.0, w: 1.2, h: 2.25, style: 'wood', ajar: 0.0 }],
    g: [{ side: 'e', u0: -27.3, u1: -24.7 }] },
  // oficina: o unico interior aberto do sul (portao de enrolar para a rua leste)
  { x0: 0.6, z0: -21.0, x1: AV_E0, z1: TV_S0, h: H_TERR, wall: 'plaster', seed: 334,
    reg: 'vila_sul', f: ['w', 'e', 'n'],
    doors: [{ side: 'e', u: -17.0, w: 3.4, h: 3.0, style: 'roll', open: 0.62 },
      { side: 'n', u: 3.0, w: 1.2, h: 2.25, style: 'metal', ajar: 1.1 }], g: [] },

  // --- quarteirao norte (centro) ------------------------------------------
  { x0: AV_W1, z0: TV_N1, x1: -0.6, z1: 21.4, h: H_TERR, wall: 'plaster', seed: 341,
    reg: 'vila_norte', f: ['s', 'w', 'e'],
    doors: [{ side: 's', u: -6.0, w: 1.2, h: 2.25, style: 'wood', ajar: 0.0 }],
    g: [{ side: 's', u0: -6.2, u1: -3.8 }, { side: 'n', u0: -6.2, u1: -3.8 }] },
  { x0: AV_W1, z0: 21.4, x1: -0.6, z1: RING, h: H_CASA, wall: 'brick', seed: 342,
    reg: 'vila_norte', f: ['w', 'e', 'n'], clean: true,
    doors: [{ side: 'n', u: -5.0, w: 1.2, h: 2.25, style: 'wood', ajar: 0.0 }],
    g: [{ side: 's', u0: -6.2, u1: -3.8 }] },
  { x0: 1.2, z0: TV_N1, x1: AV_E0, z1: 22.0, h: H_CASA, wall: 'plaster', seed: 343,
    reg: 'vila_norte', f: ['s', 'w', 'e'],
    doors: [{ side: 's', u: 5.4, w: 1.2, h: 2.25, style: 'metal', ajar: 0.0 }], g: [] },
  { x0: 1.2, z0: 22.0, x1: AV_E0, z1: RING, h: H_CASA, wall: 'plaster', seed: 344,
    reg: 'vila_norte', f: ['w', 'e', 'n'],
    doors: [{ side: 'n', u: 5.4, w: 1.2, h: 2.25, style: 'wood', ajar: 0.0 }], g: [] },

  // --- quarteirao leste ---------------------------------------------------
  { x0: AV_E1, z0: -9.6, x1: 21.6, z1: -0.6, h: H_CASA, wall: 'plaster', seed: 351,
    reg: 'vila_centro', f: ['s', 'w', 'n'],
    doors: [{ side: 'w', u: -5.4, w: 1.2, h: 2.25, style: 'wood', ajar: 0.0 }],
    g: [{ side: 'n', u0: 16.1, u1: 17.9 }] },
  { x0: 21.6, z0: -9.6, x1: RING, z1: -0.6, h: H_TERR, wall: 'brick', seed: 352,
    reg: 'vila_centro', f: ['s', 'e', 'n'],
    doors: [{ side: 's', u: 25.0, w: 1.2, h: 2.25, style: 'metal', ajar: 0.0 }], g: [] },
  { x0: AV_E1, z0: 1.2, x1: 22.4, z1: 9.6, h: H_CASA, wall: 'brick', seed: 353,
    reg: 'vila_centro', f: ['s', 'w', 'n'],
    doors: [{ side: 'n', u: 17.0, w: 1.2, h: 2.25, style: 'wood', ajar: 0.0 }],
    g: [{ side: 's', u0: 16.1, u1: 17.9 }] },
  { x0: 22.4, z0: 1.2, x1: RING, z1: 9.6, h: H_CASA, wall: 'plaster', seed: 354,
    reg: 'vila_centro', f: ['s', 'e', 'n'], clean: true,
    doors: [{ side: 'n', u: 26.0, w: 1.2, h: 2.25, style: 'wood', ajar: 0.0 }],
    g: [{ side: 'e', u0: 1.5, u1: 3.1 }] },

  // --- quarteirao sudeste -------------------------------------------------
  { x0: AV_E1, z0: -30.4, x1: 21.0, z1: -22.0, h: H_CASA, wall: 'plaster', seed: 361,
    reg: 'vila_sul', f: ['s', 'w', 'e'], clean: true,
    doors: [{ side: 's', u: 17.0, w: 1.2, h: 2.25, style: 'wood', ajar: 0.0 }],
    g: [{ side: 'n', u0: 15.8, u1: 18.2 }, { side: 'w', u0: -27.3, u1: -24.7 }] },
  { x0: AV_E1, z0: -22.0, x1: 21.0, z1: TV_S0, h: H_TERR, wall: 'brick', seed: 362,
    reg: 'vila_sul', f: ['w', 'e', 'n'],
    doors: [{ side: 'n', u: 17.0, w: 1.2, h: 2.25, style: 'metal', ajar: 0.0 }],
    g: [{ side: 'w', u0: -18.8, u1: -17.2 }, { side: 's', u0: 15.8, u1: 18.2 },
      { side: 'e', u0: -18.4, u1: -16.8 }] },
  { x0: 22.8, z0: -30.4, x1: RING, z1: -21.6, h: H_CASA, wall: 'brick', seed: 363,
    reg: 'vila_sul', f: ['s', 'w', 'e'], clean: true,
    doors: [{ side: 's', u: 26.6, w: 1.2, h: 2.25, style: 'wood', ajar: 0.0 }], g: [] },
  { x0: 22.8, z0: -21.6, x1: RING, z1: TV_S0, h: H_CASA, wall: 'plaster', seed: 364,
    reg: 'vila_sul', f: ['w', 'e', 'n'], clean: true,
    doors: [{ side: 'n', u: 26.6, w: 1.2, h: 2.25, style: 'wood', ajar: 0.0 }], g: [] },

  // --- quarteirao nordeste ------------------------------------------------
  { x0: AV_E1, z0: TV_N1, x1: 21.8, z1: 20.4, h: H_CASA, wall: 'plaster', seed: 371,
    reg: 'vila_norte', f: ['s', 'w', 'n'], clean: true,
    doors: [{ side: 'w', u: 16.0, w: 1.2, h: 2.25, style: 'wood', ajar: 0.0 }], g: [] },
  { x0: 21.8, z0: TV_N1, x1: RING, z1: 20.4, h: H_CASA, wall: 'brick', seed: 372,
    reg: 'vila_norte', f: ['s', 'e', 'n'],
    doors: [{ side: 's', u: 26.0, w: 1.2, h: 2.25, style: 'metal', ajar: 0.0 }],
    g: [{ side: 'n', u0: 25.1, u1: 26.9 }] },
  { x0: AV_E1, z0: 22.2, x1: 22.4, z1: RING, h: H_TERR, wall: 'brick', seed: 373,
    reg: 'vila_norte', f: ['s', 'w', 'n'],
    doors: [{ side: 'w', u: 26.0, w: 1.2, h: 2.25, style: 'wood', ajar: 0.0 }], g: [] },
  { x0: 22.4, z0: 22.2, x1: RING, z1: RING, h: H_CASA, wall: 'plaster', seed: 374,
    reg: 'vila_norte', f: ['s', 'e', 'n'], clean: true,
    doors: [{ side: 'n', u: 26.6, w: 1.2, h: 2.25, style: 'wood', ajar: 0.0 }],
    g: [{ side: 'n', u0: 27.9, u1: 29.6 }, { side: 's', u0: 25.1, u1: 26.9 }] },

  // --- as duas casas de dentro da praca ------------------------------------
  // mercearia: interior aberto, porta para a praca
  { x0: AV_W1, z0: -9.6, x1: -4.2, z1: -5.6, h: H_TERR, wall: 'plaster', seed: 381,
    reg: 'vila_praca', f: ['s', 'w', 'e', 'n'],
    doors: [{ side: 'n', u: -6.6, w: 1.3, h: 2.3, style: 'wood', ajar: 1.25 }],
    g: [{ side: 's', u0: -7.4, u1: -5.6 }] },
  // casa do poco: terraco de 3,6 m que domina a praca pelo lado leste
  { x0: 5.0, z0: -9.6, x1: AV_E0, z1: -4.8, h: H_TERR, wall: 'brick', seed: 382,
    reg: 'vila_praca', f: ['s', 'w', 'e', 'n'],
    doors: [{ side: 'n', u: 7.4, w: 1.2, h: 2.25, style: 'metal', ajar: 0.0 }],
    g: [{ side: 'w', u0: -5.7, u1: -4.2 }] }
];

// ---------------------------------------------------------------------------
// ajudantes locais
// ---------------------------------------------------------------------------

/**
 * Escada externa de concreto entre duas cotas. Devolve o comprimento ocupado no
 * chao (degraus + patamar), que e o que decide se ela cabe na rua.
 * rotY: 0 sobe para +Z, PI para -Z, PI/2 para +X, -PI/2 para -X.
 *
 * `railing: false` NAO e economia de detalhe: o guarda-corpo do P.makeStairs sai
 * ATRAVESSADO (P.makeRailing e construido no eixo X e a escada so o inclina em X,
 * sem o giro em Y que o P.makeCatwalk faz), entao vira uma barra do tamanho do
 * lance cruzando a rua a 1,8 m de altura. MEDIDO nas 12 escadas deste mapa: numa
 * rua de 3,6 m a barra entrava nas duas fachadas. A mureta lateral de concreto,
 * que e parte da escada, continua ali.
 */
function stairTo(level, x, z, rotY, yBase, yTop, w) {
  const run = 0.34;
  const steps = Math.max(6, Math.round((yTop - yBase) / 0.355));
  const rise = (yTop - yBase) / steps;
  level._place(P.makeStairs(level.mats, {
    steps, rise, run, w: w || 1.5, landing: 1.2, railing: false
  }), x, yBase, z, rotY);
  return steps * run + 1.2;
}

/**
 * Muro de quintal atravessando uma rua, com UM vao de passagem.
 *
 * axis 'x': o muro corre em X e fecha uma rua norte-sul; f0..f1 e a espessura em Z.
 * axis 'z': o muro corre em Z e fecha uma rua leste-oeste; f0..f1 e a espessura em X.
 *
 * O vao [g0,g1] fica sempre encostado numa das laterais, e o muro seguinte da
 * mesma rua poe o vao na outra: quem olha o corredor inteiro ve alvenaria, nao
 * o outro lado do mapa. E o mesmo truque dos bloqueios de corredor do porto,
 * escrito no vocabulario da vila.
 */
function gateWall(level, axis, u0, u1, f0, f1, h, g0, g1, seed) {
  const b = level.batch;
  const mat = (seed % 3) === 0 ? 'brick' : 'plaster';
  const lint = 2.3;
  const off = { solid: false };

  b.setVariant(seed % 4, seed * 37 + 11);
  if (axis === 'x') {
    if (g0 - u0 > 0.05) level._slab(mat, u0, -0.3, f0, g0, h, f1);
    if (u1 - g1 > 0.05) level._slab(mat, g1, -0.3, f0, u1, h, f1);
    level._slab(mat, g0, lint, f0, g1, h, f1);
  } else {
    if (g0 - u0 > 0.05) level._slab(mat, f0, -0.3, u0, f1, h, g0);
    if (u1 - g1 > 0.05) level._slab(mat, f0, -0.3, g1, f1, h, u1);
    level._slab(mat, f0, lint, g0, f1, h, g1);
  }
  b.clearVariant();

  // coroamento e verga: a aresta viva no alto e o que faz o muro ler como obra
  if (axis === 'x') {
    level._slab('concrete', u0 - 0.1, h, f0 - 0.1, u1 + 0.1, h + 0.16, f1 + 0.1, off);
    level._slab('concrete', g0 - 0.18, lint, f0 - 0.08, g1 + 0.18, lint + 0.18, f1 + 0.08, off);
  } else {
    level._slab('concrete', f0 - 0.1, h, u0 - 0.1, f1 + 0.1, h + 0.16, u1 + 0.1, off);
    level._slab('concrete', f0 - 0.08, lint, g0 - 0.18, f1 + 0.08, lint + 0.18, g1 + 0.18, off);
  }

  // metade dos muros ganha folha de portao escancarada; a outra fica so com o vao
  if ((seed % 2) === 0) {
    const cu = (g0 + g1) * 0.5;
    const leaf = P.makeDoorUnit(level.mats, {
      w: (g1 - g0) - 0.18, h: lint - 0.12, style: 'metal', ajar: 1.42
    });
    if (axis === 'x') level._place(leaf, cu, Y_ST, f0, Math.PI);
    else level._place(leaf, f0, Y_ST, cu, -Math.PI / 2);
  }
}

/**
 * Prancha de madeira ligando dois telhados sobre um beco ou uma rua.
 * axis 'x': atravessa em X entre a0 e a1, centrada em z = c.
 */
function plank(level, axis, a0, a1, c, y, w) {
  const hw = (w || 1.5) * 0.5;
  const off = { solid: false };
  if (axis === 'x') {
    level._slab('wood', a0, y - 0.12, c - hw, a1, y, c + hw);
    level._slab('wood', a0, y - 0.24, c - hw + 0.08, a1, y - 0.12, c - hw + 0.22, off);
    level._slab('wood', a0, y - 0.24, c + hw - 0.22, a1, y - 0.12, c + hw - 0.08, off);
  } else {
    level._slab('wood', c - hw, y - 0.12, a0, c + hw, y, a1);
    level._slab('wood', c - hw + 0.08, y - 0.24, a0, c - hw + 0.22, y - 0.12, a1, off);
    level._slab('wood', c + hw - 0.22, y - 0.24, a0, c + hw - 0.08, y - 0.12, a1, off);
  }
}

/**
 * Caixa de escada / casa de bomba no telhado. Existe por combate: sem um volume
 * opaco de 2,4 m aqui e ali, o nivel alto da vila vira um tabuleiro liso com
 * linha de tiro de 68 m - exatamente o que a planta de baixo evita.
 */
function roofBox(level, cx, cz, w, d, y, h, seed) {
  const b = level.batch;
  b.setVariant(seed % 4, seed * 23 + 5);
  level._slab('plaster', cx - w * 0.5, y, cz - d * 0.5, cx + w * 0.5, y + h, cz + d * 0.5);
  b.clearVariant();
  level._slab('concrete', cx - w * 0.5 - 0.12, y + h, cz - d * 0.5 - 0.12,
    cx + w * 0.5 + 0.12, y + h + 0.16, cz + d * 0.5 + 0.12, { solid: false });
  // porta de acesso, so silhueta - a subida de verdade e pela escada externa
  level._slab('metal_thin', cx - 0.45, y, cz + d * 0.5 - 0.04, cx + 0.45, y + 1.95,
    cz + d * 0.5 + 0.03, { solid: false });
}

// ---------------------------------------------------------------------------
// construcao
// ---------------------------------------------------------------------------

export function build(level) {
  buildGround(level);
  buildPerimeter(level);
  buildHouses(level);
  buildStreets(level);
  buildSquare(level);
  buildRoofRoute(level);
  buildInteriors(level);
  buildBackdrop(level);
  return true;
}

/**
 * Chao de tudo. A ordem importa: _groundY devolve o ULTIMO retangulo registrado
 * que contem o ponto, entao o terreno base entra primeiro e a praca por ultimo.
 */
function buildGround(level) {
  const b = level.batch;

  b.setRegion('vila_borda');
  // terreno base: existe chao debaixo de tudo, inclusive fora da muralha
  level._floor('gravel', MINX - 16, MINZ - 16, MAXX + 16, MAXZ + 16, 0, 1.2);

  // rua da muralha, em quatro faixas (as quatro esquinas ficam nas faixas sul/norte)
  level._floor('gravel', MINX, MINZ, MAXX, -RING, Y_ST, 0.34);
  level._floor('gravel', MINX, RING, MAXX, MAXZ, Y_ST, 0.34);
  level._floor('gravel', MINX, -RING, -RING, RING, Y_ST, 0.34);
  level._floor('gravel', RING, -RING, MAXX, RING, Y_ST, 0.34);

  b.setRegion('vila_centro');
  // ruas e travessas de terra batida
  level._floor('dirt', AV_W0, MINZ, AV_W1, MAXZ, Y_ST, 0.34);
  level._floor('dirt', AV_E0, MINZ, AV_E1, MAXZ, Y_ST, 0.34);
  level._floor('dirt', MINX, TV_S0, MAXX, TV_S1, Y_ST, 0.34);
  level._floor('dirt', MINX, TV_N0, MAXX, TV_N1, Y_ST, 0.34);
  for (let i = 0; i < BECOS.length; i++) {
    const be = BECOS[i];
    level._floor('dirt', be[0], be[1], be[2], be[3], Y_ST, 0.34);
  }
  // largo do recuo da casa 304 e patio murado do quarteirao sul
  level._floor('dirt', -14.4, -22.4, AV_W0, TV_S0, Y_ST, 0.34);
  level._floor('sand', PAT_X0, PAT_Z0, PAT_X1, PAT_Z1, Y_ST, 0.34);

  // praca: pedra no meio, degrau de concreto em volta (as quatro faixas do
  // degrau nao se sobrepoem a pedra, senao a laje de cima escondia o degrau)
  b.setRegion('vila_praca');
  level._floor('tile', -SQ, -SQ, SQ, SQ, Y_SQ, 0.5);
  level._floor('concrete', -SQ_OUT, -SQ_OUT, SQ_OUT, -SQ, Y_STEP, 0.45);
  level._floor('concrete', -SQ_OUT, SQ, SQ_OUT, SQ_OUT, Y_STEP, 0.45);
  level._floor('concrete', -SQ_OUT, -SQ, -SQ, SQ, Y_STEP, 0.45);
  level._floor('concrete', SQ, -SQ, SQ_OUT, SQ, Y_STEP, 0.45);

  // sarjeta e bueiro: o detalhe que diz que a rua e rua, e nao um corredor
  b.setRegion('vila_centro');
  const rng = P.makeRng(9701);
  for (let i = 0; i < 10; i++) {
    const on = i % 2 === 0;
    const px = on ? P.rand(rng, AV_W0 + 0.5, AV_W1 - 0.5) : P.rand(rng, AV_E0 + 0.5, AV_E1 - 0.5);
    const pz = P.rand(rng, MINZ + 4, MAXZ - 4);
    level._place(P.makeGutterGrate(level.mats, {}), px, Y_ST, pz, 0);
  }
  for (let i = 0; i < 6; i++) {
    level._place(P.makeManhole(level.mats, {}), P.rand(rng, MINX + 6, MAXX - 6),
      Y_ST, i % 2 === 0 ? P.rand(rng, TV_S0 + 0.6, TV_S1 - 0.6) : P.rand(rng, TV_N0 + 0.6, TV_N1 - 0.6), 0);
  }
}

/** Muralha fechada nos quatro lados, com contrafortes, ameias e torres de canto. */
function buildPerimeter(level) {
  const b = level.batch;
  b.setRegion('vila_borda');

  b.setVariant(2, 1717);
  level._slab('brick', MINX - WALL, -0.9, MINZ - WALL, MINX, H_WALL, MAXZ + WALL);
  level._slab('brick', MAXX, -0.9, MINZ - WALL, MAXX + WALL, H_WALL, MAXZ + WALL);
  level._slab('brick', MINX - WALL, -0.9, MINZ - WALL, MAXX + WALL, H_WALL, MINZ);
  level._slab('brick', MINX - WALL, -0.9, MAXZ, MAXX + WALL, H_WALL, MAXZ + WALL);
  b.clearVariant();

  // coroamento continuo
  const cap = { solid: false };
  level._slab('concrete', MINX - WALL - 0.14, H_WALL, MINZ - WALL - 0.14, MINX + 0.14,
    H_WALL + 0.24, MAXZ + WALL + 0.14, cap);
  level._slab('concrete', MAXX - 0.14, H_WALL, MINZ - WALL - 0.14, MAXX + WALL + 0.14,
    H_WALL + 0.24, MAXZ + WALL + 0.14, cap);
  level._slab('concrete', MINX - WALL - 0.14, H_WALL, MINZ - WALL - 0.14, MAXX + WALL + 0.14,
    H_WALL + 0.24, MINZ + 0.14, cap);
  level._slab('concrete', MINX - WALL - 0.14, H_WALL, MAXZ - 0.14, MAXX + WALL + 0.14,
    H_WALL + 0.24, MAXZ + WALL + 0.14, cap);

  // ameias e contrafortes na face de dentro
  for (let z = MINZ + 2.2; z < MAXZ - 1.5; z += 4.6) {
    level._slab('brick', MINX, H_WALL + 0.24, z, MINX + 0.34, H_WALL + 0.92, z + 2.3, cap);
    level._slab('brick', MAXX - 0.34, H_WALL + 0.24, z, MAXX, H_WALL + 0.92, z + 2.3, cap);
    level._slab('brick', MINX, -0.4, z + 1.0, MINX + 0.62, H_WALL - 0.6, z + 1.8, cap);
    level._slab('brick', MAXX - 0.62, -0.4, z + 1.0, MAXX, H_WALL - 0.6, z + 1.8, cap);
  }
  for (let x = MINX + 2.2; x < MAXX - 1.5; x += 4.6) {
    level._slab('brick', x, H_WALL + 0.24, MINZ, x + 2.3, H_WALL + 0.92, MINZ + 0.34, cap);
    level._slab('brick', x, H_WALL + 0.24, MAXZ - 0.34, x + 2.3, H_WALL + 0.92, MAXZ, cap);
    level._slab('brick', x + 1.0, -0.4, MINZ, x + 1.8, H_WALL - 0.6, MINZ + 0.62, cap);
    level._slab('brick', x + 1.0, -0.4, MAXZ - 0.62, x + 1.8, H_WALL - 0.6, MAXZ, cap);
  }

  // torres de canto: sobem 2,6 m acima da muralha e fecham a silhueta
  const towers = [[-35.2, -35.2], [35.2, -35.2], [-35.2, 35.2], [35.2, 35.2]];
  for (let i = 0; i < towers.length; i++) {
    const tx = towers[i][0];
    const tz = towers[i][1];
    b.setVariant((i + 1) % 4, 913 + i * 41);
    level._slab('brick', tx - 2.2, -0.9, tz - 2.2, tx + 2.2, H_WALL + 2.6, tz + 2.2);
    b.clearVariant();
    level._slab('concrete', tx - 2.5, H_WALL + 2.6, tz - 2.5, tx + 2.5, H_WALL + 2.9, tz + 2.5, cap);
    for (let k = 0; k < 4; k++) {
      const a = tx - 1.9 + k * 1.25;
      level._slab('brick', a, H_WALL + 2.9, tz - 2.5, a + 0.75, H_WALL + 3.5, tz - 2.2, cap);
      level._slab('brick', a, H_WALL + 2.9, tz + 2.2, a + 0.75, H_WALL + 3.5, tz + 2.5, cap);
    }
  }

  // portao da vila, fechado, no muro sul - marca a entrada sem abrir buraco
  level._slab('wood', -3.2, -0.2, MINZ - 0.12, 3.2, 4.0, MINZ + 0.06);
  for (let i = 0; i < 7; i++) {
    level._slab('metal', -3.0 + i * 0.9, 0.1, MINZ - 0.2, -2.86 + i * 0.9, 3.8, MINZ - 0.1,
      { solid: false, cast: false });
  }
  level._slab('concrete', -4.0, 4.0, MINZ - 0.3, 4.0, 4.6, MINZ + 0.2, cap);

  // iluminacao publica e fiacao ao longo da rua da muralha
  const poles = [[-31.6, -26.0], [-31.6, 8.0], [31.6, -8.0], [31.6, 24.0],
    [-14.0, -31.8], [16.0, -31.8], [-8.0, 31.8], [22.0, 31.8]];
  for (let i = 0; i < poles.length; i++) {
    const px = poles[i][0];
    const pz = poles[i][1];
    const rot = Math.abs(px) > 20 ? (px > 0 ? -Math.PI / 2 : Math.PI / 2) : (pz > 0 ? Math.PI : 0);
    level._place(P.makePole(level.mats, { h: 7.4, transformer: i === 2 || i === 5 }),
      px, Y_ST, pz, rot);
  }
  for (let i = 0; i < poles.length - 1; i += 2) {
    const a = poles[i];
    const c = poles[i + 1];
    level.batch.addObject(P.makeWire(level.mats, a[0], 6.4, a[1], c[0], 6.4, c[1], 0.9, 0.02));
  }
}

/** As 34 casas, com telhado caminhavel e bagunca de laje por cima. */
function buildHouses(level) {
  for (let i = 0; i < HOUSES.length; i++) {
    const c = HOUSES[i];
    level._building({
      x0: c.x0, x1: c.x1, z0: c.z0, z1: c.z1, h: c.h, wall: c.wall,
      faces: c.f, seed: c.seed, region: c.reg, doors: c.doors, gaps: c.g,
      parapet: c.h > 5 ? 1.0 : 0.86,
      wallT: WIN.wallT, winW: WIN.winW, winH: WIN.winH, winSpacing: WIN.winSpacing,
      winMargin: WIN.winMargin, sill: WIN.sill
    });
    // tralha de telhado: caixa d'agua, antena, varal e chamine sao a cobertura
    // do nivel alto. O retangulo entra encolhido 1,4 m para nada nascer em cima
    // da platibanda nem tapar a falha por onde a escada chega.
    const rx0 = c.x0 + 1.4;
    const rz0 = c.z0 + 1.4;
    const rx1 = c.x1 - 1.4;
    const rz1 = c.z1 - 1.4;
    if (rx1 - rx0 > 2.0 && rz1 - rz0 > 2.0) {
      // telhado que recebe caixa de escada (roofBox) leva receita 'vazia': ali a
      // cobertura ja e o volume opaco, e caixa d'agua sorteada em cima dele
      // atravessaria a alvenaria
      level._roofClutter(rx0, rz0, rx1, rz1, c.h, c.seed * 7 + 3,
        c.h > 5 ? 0.8 : 0.55, c.clean ? 'vazia' : undefined);
    }
  }
}

/**
 * O que acontece NA rua: os muros de quintal atravessados (que sao o que impede
 * a linha de tiro de ponta a ponta), toldos, escadas externas e a tralha de
 * beco. Sem isso o tabuleiro seria um labirinto limpo demais.
 */
function buildStreets(level) {
  const b = level.batch;
  b.setRegion('vila_centro');

  /**
   * Muros atravessados. axis, u0, u1 (extensao do muro), f0, f1 (espessura),
   * altura, g0, g1 (o vao). O vao alterna de lado ao longo de cada rua.
   */
  const gates = [
    // rua norte-sul oeste
    ['x', AV_W0, AV_W1, -24.2, -23.85, 3.2, AV_W0, -11.6, 11],
    ['x', AV_W0, AV_W1, -2.8, -2.45, 3.0, -11.2, AV_W1, 12],
    ['x', AV_W0, AV_W1, 19.8, 20.15, 3.2, AV_W0, -11.6, 13],
    // rua norte-sul leste
    ['x', AV_E0, AV_E1, -18.7, -18.35, 3.0, 11.6, AV_E1, 14],
    ['x', AV_E0, AV_E1, 3.0, 3.35, 3.2, AV_E0, 11.2, 15],
    ['x', AV_E0, AV_E1, 27.4, 27.75, 3.0, 11.6, AV_E1, 16],
    // travessa sul
    ['z', TV_S0, TV_S1, -25.7, -25.35, 3.2, -10.9, TV_S1, 17],
    ['z', TV_S0, TV_S1, -3.2, -2.85, 3.0, TV_S0, -11.6, 18],
    ['z', TV_S0, TV_S1, 24.0, 24.35, 3.2, -10.9, TV_S1, 19],
    // travessa norte
    ['z', TV_N0, TV_N1, -19.2, -18.85, 3.0, TV_N0, 11.2, 20],
    ['z', TV_N0, TV_N1, 5.0, 5.35, 3.2, 11.6, TV_N1, 21],
    ['z', TV_N0, TV_N1, 27.0, 27.35, 3.0, TV_N0, 11.2, 22],
    /**
     * Becos sul e norte: sao o MESMO eixo norte-sul e passam pela praca, que e
     * aberta. MEDIDO antes destes dois muros: 67,8 m de visada limpa de uma
     * ponta a outra do mapa - a unica reta assim que existia. O vao do beco sul
     * fica na metade oeste e o do norte na metade leste, e as duas metades nao
     * se cruzam: nenhum x atravessa os dois.
     */
    ['x', -1.2, 0.6, -24.0, -23.65, 2.8, -1.2, -0.28, 41],
    ['x', -0.6, 1.2, 24.0, 24.35, 2.8, 0.28, 1.2, 43],
    /**
     * Rua da muralha: quatro muros por lado, com o vao alternando entre a face
     * de fora (junto a muralha) e a de dentro (junto as casas). Nenhum deles cai
     * na boca de rua nem em cima de escada - por isso as posicoes nao sao
     * simetricas.
     */
    ['x', MINX, -RING, -25.5, -25.15, 3.0, MINX, -32.4, 23],
    ['x', MINX, -RING, -8.0, -7.65, 3.0, -32.0, -RING, 24],
    ['x', MINX, -RING, 5.0, 5.35, 3.0, MINX, -32.4, 25],
    ['x', MINX, -RING, 17.5, 17.85, 3.0, -32.0, -RING, 26],
    ['x', RING, MAXX, -20.0, -19.65, 3.0, 32.4, MAXX, 27],
    ['x', RING, MAXX, -8.0, -7.65, 3.0, RING, 32.0, 28],
    ['x', RING, MAXX, 15.5, 15.85, 3.0, 32.4, MAXX, 29],
    ['x', RING, MAXX, 24.0, 24.35, 3.0, RING, 32.0, 30],
    ['z', MINZ, -RING, -26.0, -25.65, 3.0, MINZ, -32.4, 31],
    ['z', MINZ, -RING, -16.5, -16.15, 3.0, -32.0, -RING, 32],
    ['z', MINZ, -RING, 6.0, 6.35, 3.0, MINZ, -32.4, 33],
    ['z', MINZ, -RING, 17.0, 17.35, 3.0, -32.0, -RING, 34],
    ['z', RING, MAXZ, -24.0, -23.65, 3.0, 32.4, MAXZ, 35],
    ['z', RING, MAXZ, -6.0, -5.65, 3.0, RING, 32.0, 36],
    ['z', RING, MAXZ, 8.0, 8.35, 3.0, 32.4, MAXZ, 37],
    ['z', RING, MAXZ, 19.0, 19.35, 3.0, RING, 32.0, 38]
  ];
  for (let i = 0; i < gates.length; i++) {
    const g = gates[i];
    gateWall(level, g[0], g[1], g[2], g[3], g[4], g[5], g[6], g[7], g[8]);
  }

  /**
   * Toldos sobre as portas de rua. [x, z, rotY] com a MESMA convencao de face do
   * _building: 's' vira PI, 'n' vira 0, 'w' vira -PI/2 e 'e' vira PI/2 - toldo
   * com o rotY trocado projeta para dentro da casa e some.
   */
  const awn = [
    [AV_W0, -5.4, Math.PI / 2],    // fachada leste da 312, sobre a rua oeste
    [AV_W0, 5.0, Math.PI / 2],     // fachada leste da 314
    [AV_E1, -5.4, -Math.PI / 2],   // fachada oeste da 351, sobre a rua leste
    [AV_E1, 16.0, -Math.PI / 2],   // fachada oeste da 371
    [-6.6, TV_S1, Math.PI],        // fachada sul da mercearia, sobre a travessa
    [17.0, TV_S0, 0],              // fachada norte da 362
    [-6.0, TV_N1, Math.PI]         // fachada sul da 341
  ];
  for (let i = 0; i < awn.length; i++) {
    level._place(P.makeAwning(level.mats, { w: 2.8, d: 1.3, drop: 0.55, rng: P.makeRng(400 + i) }),
      awn[i][0], 2.9, awn[i][1], awn[i][2]);
  }
  level._place(P.makeSign(level.mats, { w: 1.8, h: 0.5 }), AV_E0 + 0.12, 3.4, -17.0, Math.PI / 2);
  level._place(P.makeSign(level.mats, { w: 1.5, h: 0.45 }), -4.08, 2.6, -7.6, Math.PI / 2);

  // varais atravessando os becos - a marca da vila vista de baixo
  const lines = [[-21.9, -19.0, 1.8, 0], [-21.9, -25.0, 1.8, 0], [21.9, -18.0, 1.8, 0],
    [-0.3, -24.0, 1.8, 0], [0.3, 24.0, 1.8, 0],
    [-24.0, 21.3, 1.8, Math.PI / 2], [24.0, 21.3, 1.8, Math.PI / 2],
    [-24.0, -0.3, 1.8, Math.PI / 2], [24.0, 0.3, 1.8, Math.PI / 2]];
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    level._place(P.makeLaundryLine(level.mats, { len: l[2], rng: P.makeRng(430 + i) }),
      l[0], 3.9 + (i % 2) * 0.5, l[1], l[3]);
  }

  // tralha de rua: tambor, caixote, entulho no pe do muro e lixeira
  const rng = P.makeRng(5150);
  const spots = [
    [-11.6, -30.0], [-11.0, -14.6], [-11.6, 8.0], [-11.0, 26.4], [11.0, -28.0],
    [11.6, -8.0], [11.0, 14.0], [11.6, 29.0], [-27.0, -11.0], [-16.0, -11.6],
    [4.0, -11.0], [27.0, -11.6], [-24.0, 11.0], [-2.0, 11.6], [20.0, 11.0],
    [-21.9, -27.0], [21.9, -25.0], [-0.3, -17.0], [0.3, 27.0], [-26.0, 21.3],
    [26.0, 21.3], [-26.0, -0.3], [26.0, 0.3], [-32.0, -22.0], [-32.0, 20.0],
    [32.0, -24.0], [32.0, 18.0], [-20.0, -32.0], [8.0, -32.0], [-18.0, 32.0], [12.0, 32.0]
  ];
  for (let i = 0; i < spots.length; i++) {
    const sx = spots[i][0];
    const sz = spots[i][1];
    const r = rng();
    if (r < 0.3) {
      for (let k = 0; k < 2 + Math.floor(rng() * 2); k++) {
        level._place(P.makeBarrel(level.mats, { variant: k === 1 ? 1 : 0 }),
          sx + P.rand(rng, -0.35, 0.35), Y_ST, sz + P.rand(rng, -0.35, 0.35), rng() * 3.1);
      }
    } else if (r < 0.55) {
      level._place(P.makeCrate(level.mats, { variant: i % 4 }), sx, Y_ST, sz, rng() * 3.1);
      if (rng() > 0.5) {
        level._place(P.makeCrate(level.mats, { variant: (i + 1) % 4, w: 0.7, h: 0.55, d: 0.7 }),
          sx + 0.12, Y_ST + 0.72, sz - 0.08, rng() * 3.1);
      }
    } else if (r < 0.72) {
      level._place(P.makeRubblePile(level.mats, { r: P.rand(rng, 0.9, 1.5), h: P.rand(rng, 0.3, 0.5),
        rng: P.makeRng(600 + i), count: 14 }), sx, Y_ST, sz, 0);
    } else if (r < 0.86) {
      level._place(P.makeDumpster(level.mats, {}), sx, Y_ST, sz, rng() * 3.1);
    } else {
      level._place(P.makePlanter(level.mats, { w: 1.4, d: 0.75, rng: P.makeRng(620 + i) }),
        sx, Y_ST, sz, rng() * 3.1);
    }
  }

  // dois veiculos parados na rua da muralha: escala e cobertura de corpo inteiro
  level._place(P.makeVehicle(level.mats, { kind: 'pickup', doorOpen: true }),
    -32.0, Y_ST, -4.0, 0.06);
  level._place(P.makeVehicle(level.mats, { kind: 'sedan', burned: true }),
    2.0, Y_ST, 32.0, Math.PI / 2 + 0.1);
  for (let i = 0; i < 3; i++) {
    level._place(P.makeJerseyBarrier(level.mats, { len: 2.4 }), 26.0 + i * 2.6, Y_ST, -32.2, 0);
  }
  for (let i = 0; i < 4; i++) {
    level._place(P.makeTire(level.mats, {}), 12.4, Y_ST + i * 0.19, -12.0, rng() * 3.1);
  }

  // muro do patio do quarteirao sul: fecha o patio menos na boca do beco
  b.setRegion('vila_sul');
  level._slab('brick', PAT_X1 - 0.26, -0.2, PAT_Z0, PAT_X1, 2.1, -18.6);
  level._slab('brick', PAT_X0, -0.2, PAT_Z1 - 0.26, -2.4, 2.1, PAT_Z1);
  level._slab('concrete', PAT_X1 - 0.36, 2.1, PAT_Z0, PAT_X1 + 0.1, 2.26, -18.6,
    { solid: false });
  level._place(P.makeWorkbench(level.mats, { w: 1.8 }), -2.3, Y_ST, -21.4, Math.PI / 2);
  level._place(P.makeTarpStack(level.mats, { count: 2, rng: P.makeRng(651) }), -2.4, Y_ST, -14.6, 0.5);
}

/**
 * A praca: fonte, feira, arcada coberta e a torre. E o unico vao aberto do mapa
 * (17 x 17 m), entao TODA a cobertura dele e mobiliario - sem barraca, floreira
 * e fonte no meio, a praca seria o oposto do resto da vila.
 */
function buildSquare(level) {
  const b = level.batch;
  b.setRegion('vila_praca');
  const rng = P.makeRng(7373);

  // fonte seca no meio, deslocada do eixo para a praca nao ficar simetrica
  level._place(P.makeFountain(level.mats, { r: 2.1 }), -1.2, Y_SQ, -0.6, 0);
  for (let i = 0; i < 4; i++) {
    level._place(P.makePlanter(level.mats, { w: 1.6, d: 0.8, rng: P.makeRng(700 + i) }),
      -1.2 + Math.cos(i * 1.57 + 0.6) * 4.2, Y_SQ, -0.6 + Math.sin(i * 1.57 + 0.6) * 4.2,
      i * 1.57);
  }

  // feira: barracas em duas fileiras curtas, com corredor entre elas. A fileira
  // oeste comeca em z=-3,6 porque a mercearia ocupa a praca ate z=-5,6
  for (let i = 0; i < 3; i++) {
    level._place(P.makeMarketStall(level.mats, { w: 2.4, d: 1.5, rng: P.makeRng(710 + i) }),
      -6.6 + i * 0.3, Y_SQ, -3.6 + i * 2.8, 0.06 * i);
    level._place(P.makeMarketStall(level.mats, { w: 2.2, d: 1.4, rng: P.makeRng(720 + i) }),
      4.6 - i * 0.4, Y_SQ, 0.4 + i * 2.6, Math.PI - 0.05 * i);
  }
  // toldo da feira preso na fachada norte da mercearia (toldo solto no meio da
  // praca ficaria com os tirantes no ar)
  level._place(P.makeAwning(level.mats, { w: 4.0, d: 1.8, drop: 0.6, rng: P.makeRng(730) }),
    -6.8, 2.9, -5.6, 0);

  // palmeiras: a vertical viva que a praca precisa para nao ler como patio
  level._place(P.makePalm(level.mats, { h: 6.2, rng: P.makeRng(741) }), 2.6, Y_SQ, -6.6, 0.4);
  level._place(P.makePalm(level.mats, { h: 5.4, rng: P.makeRng(742) }), -7.4, Y_SQ, 3.4, 1.2);

  /**
   * Monumento da praca, no eixo x=0.
   *
   * Nao e enfeite: os becos sul e norte, as duas travessas e a praca formam um
   * unico eixo norte-sul, e o pedaco aberto da praca era o elo que deixava a
   * visada correr. MEDIDO sem ele: 67,8 m de linha limpa de ponta a ponta, a
   * pior do mapa. A base de 2,2 m cobre toda a faixa de x que os dois becos tem
   * em comum, entao nenhuma reta desse eixo passa inteira.
   */
  const mx = 0.0, mz = 3.2;
  level._slab('concrete', mx - 1.2, Y_SQ - 0.1, mz - 1.2, mx + 1.2, Y_SQ + 0.28, mz + 1.2);
  level._slab('concrete', mx - 1.1, Y_SQ + 0.28, mz - 1.1, mx + 1.1, Y_SQ + 0.6, mz + 1.1);
  b.setVariant(3, 7711);
  // o bloco do pedestal tem 2,0 m de largura e 2,15 m de altura: cobre a faixa
  // de x que os becos tem em comum ACIMA da linha do olho, que e o que fecha o
  // eixo. Pedestal baixo so daria cobertura, nao corte de visada.
  level._slab('brick', mx - 1.0, Y_SQ + 0.6, mz - 1.0, mx + 1.0, 2.15, mz + 1.0);
  b.clearVariant();
  level._slab('concrete', mx - 1.12, 2.15, mz - 1.12, mx + 1.12, 2.42, mz + 1.12, { solid: false });
  level._slab('concrete', mx - 0.42, 2.42, mz - 0.42, mx + 0.42, 3.9, mz + 0.42);
  level._slab('concrete', mx - 0.56, 3.9, mz - 0.56, mx + 0.56, 4.16, mz + 0.56, { solid: false });
  level._slab('concrete', mx - 0.18, 4.16, mz - 0.18, mx + 0.18, 4.68, mz + 0.18, { solid: false });
  level._place(P.makeSign(level.mats, { w: 0.9, h: 0.4 }), mx, 1.5, mz - 1.02, Math.PI);

  // --- arcada (alpendre) na borda norte -----------------------------------
  // parede de fundo com passagem para a travessa norte
  level._slab('plaster', ARC_X0, Y_STEP - 0.2, ARC_Z1 - 0.4, -2.0, ARC_Y, ARC_Z1);
  level._slab('plaster', 0.0, Y_STEP - 0.2, ARC_Z1 - 0.4, ARC_X1, ARC_Y, ARC_Z1);
  level._slab('plaster', -2.0, 2.35, ARC_Z1 - 0.4, 0.0, ARC_Y, ARC_Z1);
  for (let i = 0; i < 6; i++) {
    level._place(P.makeColumn(level.mats, { h: ARC_Y - Y_SQ, r: 0.26 }),
      ARC_X0 + 0.8 + i * 2.0, Y_SQ, ARC_Z0 + 0.7, 0);
  }
  level._slab('concrete', ARC_X0, ARC_Y, ARC_Z0, ARC_X1, ARC_TOP, ARC_Z1);
  level._slab('concrete', ARC_X0 - 0.16, ARC_Y - 0.22, ARC_Z0 - 0.16, ARC_X1 + 0.16, ARC_Y,
    ARC_Z0 + 0.1, { solid: false });
  // guarda-corpo do alpendre, com falha onde a prancha atravessa a travessa
  level._place(P.makeRailing(level.mats, { len: 4.4, h: 0.98, mesh: true }),
    ARC_X0 + 2.3, ARC_TOP, ARC_Z0 + 0.2, 0);
  level._place(P.makeRailing(level.mats, { len: 3.2, h: 0.98, mesh: true }),
    ARC_X1 - 1.7, ARC_TOP, ARC_Z0 + 0.2, 0);
  level._place(P.makeSandbagWall(level.mats, { len: 2.8, rows: 3, rng: P.makeRng(751) }),
    -0.5, ARC_TOP, ARC_Z1 - 0.9, 0);
  level._place(P.makeHangingLamp(level.mats, { len: 0.7 }), -3.0, ARC_Y - 0.1, 7.8, 0);
  level._place(P.makeCrate(level.mats, { variant: 2 }), ARC_X0 + 1.2, Y_SQ, ARC_Z1 - 1.2, 0.4);

  // escada da praca para o alpendre
  stairTo(level, -8.8, 2.0, 0, Y_SQ, ARC_TOP, 1.5);

  // --- torre do relogio ----------------------------------------------------
  b.setVariant(1, 8181);
  level._slab('brick', TWR_X0, -0.4, TWR_Z0, TWR_X1, 2.6, TWR_Z1);
  level._slab('plaster', TWR_X0 + 0.12, 2.6, TWR_Z0 + 0.12, TWR_X1 - 0.12, 8.4, TWR_Z1 - 0.12);
  b.clearVariant();
  const cap = { solid: false };
  level._slab('concrete', TWR_X0 - 0.08, 2.6, TWR_Z0 - 0.08, TWR_X1 + 0.08, 2.82, TWR_Z1 + 0.08, cap);
  // galeria: a sacada que quebra o prisma e joga sombra na praca
  level._slab('concrete', TWR_X0 - 0.6, 8.4, TWR_Z0 - 0.6, TWR_X1 + 0.6, 8.8, TWR_Z1 + 0.6);
  level._place(P.makeRailing(level.mats, { len: 4.2, h: 0.9, mesh: true }),
    (TWR_X0 + TWR_X1) * 0.5, 8.8, TWR_Z0 - 0.5, 0);
  level._place(P.makeRailing(level.mats, { len: 4.2, h: 0.9, mesh: true }),
    (TWR_X0 + TWR_X1) * 0.5, 8.8, TWR_Z1 + 0.5, 0);
  level._slab('plaster', TWR_X0 + 0.35, 8.8, TWR_Z0 + 0.35, TWR_X1 - 0.35, TWR_H, TWR_Z1 - 0.35);
  level._slab('concrete', TWR_X0 + 0.1, TWR_H, TWR_Z0 + 0.1, TWR_X1 - 0.1, TWR_H + 0.34,
    TWR_Z1 - 0.1, cap);
  // vaos do campanario nas quatro faces
  for (let i = 0; i < 4; i++) {
    const a = i % 2 === 0;
    const s = i < 2 ? -1 : 1;
    if (a) {
      level._slab('glass', TWR_X0 + 0.9, 9.3, s > 0 ? TWR_Z1 - 0.42 : TWR_Z0 + 0.32,
        TWR_X1 - 0.9, 10.6, s > 0 ? TWR_Z1 - 0.32 : TWR_Z0 + 0.42, { solid: false, cast: false });
    } else {
      level._slab('glass', s > 0 ? TWR_X1 - 0.42 : TWR_X0 + 0.32, 9.3, TWR_Z0 + 0.9,
        s > 0 ? TWR_X1 - 0.32 : TWR_X0 + 0.42, 10.6, TWR_Z1 - 0.9, { solid: false, cast: false });
    }
  }
  // mostrador do relogio, virado para a praca
  level._slab('plaster', TWR_X0 + 0.5, 6.2, TWR_Z0 - 0.1, TWR_X1 - 0.5, 7.4, TWR_Z0 + 0.04, cap);
  level._slab('metal_dull', TWR_X0 + 0.7, 6.4, TWR_Z0 - 0.16, TWR_X1 - 0.7, 7.2,
    TWR_Z0 - 0.08, cap);

  // poco da praca, ao pe da casa do poco, e a escada do terraco dela
  level._place(P.makeWaterTank(level.mats, { r: 0.85, h: 1.0, legs: false }), 6.4, Y_SQ, -3.4, 0.3);
  level._place(P.makeBarrel(level.mats, { variant: 2 }), 7.6, Y_SQ, -2.2, 0.8);
  level._place(P.makeTarpStack(level.mats, { count: 3, rng: P.makeRng(761) }), 1.4, Y_SQ, 8.2, 0.3);
  level._place(P.makeRubblePile(level.mats, { r: 1.3, h: 0.45, rng: P.makeRng(762), count: 16 }),
    7.2, Y_SQ, 1.2, 0);
  stairTo(level, 4.0, -8.6, 0, Y_SQ, H_TERR, 1.5);

  // pintura gasta do piso: as duas faixas que sobraram da via que cruzava a praca
  const line = { solid: false, cast: false };
  for (let z = -SQ + 1.0; z < SQ - 1.0; z += 2.6) {
    level._slab('asphalt_line', 1.6, Y_SQ, z, 1.86, Y_SQ + 0.012, z + 1.3, line);
  }
  for (let i = 0; i < 16; i++) {
    const px = P.rand(rng, -SQ + 1, SQ - 1);
    const pz = P.rand(rng, -SQ + 1, SQ - 1);
    const r = P.rand(rng, 0.4, 1.3);
    level._slab('dirt', px - r, Y_SQ, pz - r * 0.8, px + r, Y_SQ + 0.01, pz + r * 0.8, line);
  }
}

/**
 * A rota alta: escadas externas da rua para o terraco, lances curtos de terraco
 * para telhado e as pranchas que atravessam beco e rua. Cada falha de platibanda
 * declarada em HOUSES corresponde a uma chegada daqui - e por isso que a lista de
 * `g` la em cima nao pode ser mexida sem mexer aqui.
 */
function buildRoofRoute(level) {
  const b = level.batch;

  // --- sudoeste: rua da muralha -> terraco 302 -> telhado 301 -> 303 -> 331 --
  // o x das escadas de rua nao e redondo de proposito: o patamar tem 2,0 m de
  // largura e precisa ENCOSTAR na fachada, senao sobra uma fresta entre o
  // patamar e o telhado
  b.setRegion('vila_sul');
  stairTo(level, -31.4, -20.0, 0, Y_ST, H_TERR, 1.5);
  stairTo(level, -26.5, -17.88, Math.PI, H_TERR, H_CASA, 1.5);
  plank(level, 'x', -23.0, -20.8, -26.0, H_CASA, 1.6);           // beco sudoeste
  level._place(P.makeCatwalk(level.mats, { len: 4.0, w: 1.35 }),
    -11.4, H_CASA, -26.0, Math.PI / 2);                          // sobre a rua oeste

  // --- oeste: rua oeste -> terraco 312 -> telhado 311 ----------------------
  b.setRegion('vila_centro');
  stairTo(level, -12.1, -8.0, 0, Y_ST, H_TERR, 1.5);
  stairTo(level, -17.88, -5.4, -Math.PI / 2, H_TERR, H_CASA, 1.5);

  // --- sul: patio -> terraco 332 -> prancha -> telhado da mercearia --------
  b.setRegion('vila_sul');
  stairTo(level, -2.45, -20.0, 0, Y_ST, H_TERR, 1.4);
  level._place(P.makeCatwalk(level.mats, { len: 4.0, w: 1.35 }), -6.5, H_TERR, -11.4, 0);

  // --- sudeste: rua leste -> terraco 362 -> telhado 361 -> 333 -------------
  stairTo(level, 12.4, -14.0, Math.PI, Y_ST, H_TERR, 1.5);
  stairTo(level, 17.0, -18.08, Math.PI, H_TERR, H_CASA, 1.5);
  level._place(P.makeCatwalk(level.mats, { len: 4.0, w: 1.35 }),
    11.4, H_CASA, -26.0, Math.PI / 2);                           // sobre a rua leste
  // rampa de entulho no beco sudeste: atalho para o mesmo terraco, sem escada.
  // A cunha do prop sobe em +X, entao o rotY que a faz subir para o NORTE e
  // -PI/2 (o mesmo giro que leva o +Z da escada para -X) - com +PI/2 ela subia
  // para o sul e morria na parede de 6,4 m, sem apoio nenhum no topo.
  level._place(P.makeRubbleRamp(level.mats, { len: 5.0, w: 1.7, rise: H_TERR - 0.06,
    rng: P.makeRng(802) }), 21.9, Y_ST, -22.6, -Math.PI / 2);

  // --- leste: rua da muralha -> telhado 354 -> prancha para 353 ------------
  b.setRegion('vila_centro');
  stairTo(level, 31.4, 9.0, Math.PI, Y_ST, H_CASA, 1.5);
  plank(level, 'z', -0.7, 1.3, 17.0, H_CASA, 1.6);               // beco leste

  // --- noroeste: rua da muralha -> telhado 323 -> prancha para 321 ---------
  b.setRegion('vila_norte');
  stairTo(level, -31.4, 22.0, 0, Y_ST, H_CASA, 1.5);
  plank(level, 'z', 20.3, 22.3, -26.0, H_CASA, 1.6);             // beco noroeste

  // --- nordeste: rua da muralha -> telhado 374 -> prancha para 372 ---------
  stairTo(level, 22.0, 31.4, Math.PI / 2, Y_ST, H_CASA, 1.5);
  plank(level, 'z', 20.3, 22.3, 26.0, H_CASA, 1.6);              // beco nordeste

  // --- norte: alpendre -> terraco 341 -> telhado 342 -----------------------
  // 4,6 m de passarela para 3,6 m de rua: sobra meio metro de apoio em cada
  // ponta, que e o que impede o degrau de virar buraco
  level._place(P.makeCatwalk(level.mats, { len: 4.6, w: 1.35 }), -5.0, ARC_TOP - 0.06, 11.4, 0);
  stairTo(level, -5.0, 17.48, 0, H_TERR, H_CASA, 1.5);

  // --- volumes opacos no nivel alto ---------------------------------------
  // cada um corta uma linha de tiro de telhado que a platibanda sozinha nao
  // corta: a 8,05 m (olho de quem esta em pe no telhado) a platibanda de 1,0 m
  // ja passou por baixo
  roofBox(level, -26.6, -25.0, 2.6, 2.4, H_CASA, 2.4, 31);
  roofBox(level, -25.0, 5.0, 2.4, 2.6, H_CASA, 2.4, 32);
  roofBox(level, -27.6, 27.6, 2.6, 2.4, H_CASA, 2.4, 33);
  roofBox(level, 5.4, -26.0, 2.4, 2.6, H_CASA, 2.4, 34);
  roofBox(level, 17.6, -26.6, 2.6, 2.4, H_CASA, 2.4, 35);
  roofBox(level, 26.0, 5.4, 2.4, 2.6, H_CASA, 2.4, 36);
  roofBox(level, 17.0, 16.4, 2.6, 2.4, H_CASA, 2.4, 37);
  roofBox(level, -5.0, 25.0, 2.4, 2.6, H_CASA, 2.4, 38);
  /**
   * Caixas de borda. MEDIDO sem elas: 66 m de visada limpa a 8,05 m rente ao
   * alinhamento dos telhados de borda (x=-30, x=+30, z=+-30) - a faixa onde a
   * platibanda fica logo abaixo do olho. Sao menores (1,6 m) porque ali o volume
   * so precisa cortar a faixa de um telhado, nao o meio do quarteirao.
   */
  roofBox(level, -29.6, -29.6, 1.6, 1.6, H_CASA, 2.2, 51);
  roofBox(level, -29.6, 2.6, 1.6, 1.6, H_CASA, 2.2, 52);
  roofBox(level, -29.6, 24.6, 1.6, 1.6, H_CASA, 2.2, 53);
  roofBox(level, 2.4, -29.6, 1.6, 1.6, H_CASA, 2.2, 54);
  roofBox(level, 19.6, -29.6, 1.6, 1.6, H_CASA, 2.2, 55);
  roofBox(level, 29.6, -26.0, 1.6, 1.6, H_CASA, 2.2, 56);
  roofBox(level, 29.6, 4.0, 1.6, 1.6, H_CASA, 2.2, 57);
  roofBox(level, 29.6, 26.0, 1.6, 1.6, H_CASA, 2.2, 58);
  roofBox(level, -8.4, 29.6, 1.6, 1.6, H_CASA, 2.2, 59);
  // caixas de meio de faixa: fecham a fresta que sobrava entre a caixa de borda
  // e a caixa do meio do quarteirao (medido: 60 m de visada em x=-28,5)
  roofBox(level, -23.0, -8.0, 2.0, 2.0, H_CASA, 2.2, 61);
  roofBox(level, -17.0, -28.3, 2.0, 2.0, H_CASA, 2.2, 62);
  roofBox(level, -18.0, 23.0, 2.0, 2.0, H_CASA, 2.2, 63);
  roofBox(level, 23.8, -19.0, 2.0, 2.0, H_CASA, 2.2, 64);
  roofBox(level, 28.0, -16.0, 2.0, 2.0, H_CASA, 2.2, 65);

  // cobertura leve na rota: saco de areia e caixote nos pontos de chegada
  const sb = [[-26.5, -22.6, 0], [-19.0, -5.4, Math.PI / 2], [-6.5, -8.0, 0],
    [17.0, -23.0, 0], [26.0, 3.0, Math.PI / 2], [-26.0, 23.6, 0], [26.6, 24.0, 0],
    [-5.0, 22.4, 0], [17.5, 3.4, Math.PI / 2]];
  for (let i = 0; i < sb.length; i++) {
    level._place(P.makeSandbagWall(level.mats, { len: 3.0, rows: 3, rng: P.makeRng(820 + i) }),
      sb[i][0], H_CASA, sb[i][1], sb[i][2]);
  }
  level._place(P.makeSandbagWall(level.mats, { len: 2.6, rows: 3, rng: P.makeRng(841) }),
    -17.0, H_TERR, -4.0, Math.PI / 2);
  level._place(P.makeSandbagWall(level.mats, { len: 2.6, rows: 3, rng: P.makeRng(842) }),
    16.4, H_TERR, -19.0, Math.PI / 2);
  level._place(P.makeTarpStack(level.mats, { count: 2, rng: P.makeRng(843) }),
    -6.8, H_TERR, -7.4, 0.4);
  level._place(P.makeCrate(level.mats, { variant: 1 }), -26.0, H_TERR, -17.4, 0.5);
  level._place(P.makeCrate(level.mats, { variant: 3, w: 0.7, h: 0.56, d: 0.7 }),
    -25.7, H_TERR + 0.72, -17.3, 1.0);
}

/**
 * Os dois interiores abertos: a oficina do quarteirao sul e a mercearia da
 * praca. Os outros 32 volumes tem porta fechada de proposito - interior vazio
 * atras de cada porta seria pior do que porta que nao abre.
 */
function buildInteriors(level) {
  const b = level.batch;
  const t = WIN.wallT;

  // --- oficina (casa 334), portao de enrolar para a rua leste --------------
  b.setRegion('vila_sul');
  const ox0 = 0.6 + t, oz0 = -21.0 + t, ox1 = AV_E0 - t, oz1 = TV_S0 - t;
  level._floor('concrete', ox0, oz0, ox1, oz1, Y_IN, 0.4);
  level._place(P.makeWorkbench(level.mats, { w: 2.6 }), 2.6, Y_IN, oz0 + 0.9, 0);
  level._place(P.makeShelf(level.mats, { w: 2.4, h: 2.2, d: 0.7 }), ox0 + 1.4, Y_IN, -17.0,
    Math.PI / 2);
  level._place(P.makeShelf(level.mats, { w: 2.2, h: 2.0, d: 0.6 }), 5.4, Y_IN, oz1 - 0.5, Math.PI);
  level._place(P.makeVehicle(level.mats, { kind: 'sedan', burned: false, doorOpen: true }),
    6.0, Y_IN, -17.4, Math.PI / 2 + 0.05);
  const rng = P.makeRng(9911);
  for (let i = 0; i < 4; i++) {
    level._place(P.makeBarrel(level.mats, { variant: i === 2 ? 1 : 0 }),
      2.0 + i * 0.62, Y_IN, -14.6, rng() * 3.1);
  }
  for (let k = 0; k < 5; k++) {
    level._place(P.makeTire(level.mats, {}), 8.2, Y_IN + k * 0.19, -19.8, rng() * 3.1);
  }
  level._place(P.makePallet(level.mats), 4.0, Y_IN, -19.6, 0.3);
  level._place(P.makeCrate(level.mats, { variant: 0 }), 4.0, Y_IN + 0.11, -19.6, 0.5);
  level._place(P.makeHangingLamp(level.mats, { len: 0.9 }), 5.0, H_TERR - 0.4, -17.0, 0);
  level._place(P.makeHangingLamp(level.mats, { len: 0.9 }), 5.0, H_TERR - 0.4, -20.0, 0);
  level._interiorDust(ox0, oz0, ox1, oz1, Y_IN, 1.0);

  // --- mercearia (casa 381), porta para a praca ---------------------------
  b.setRegion('vila_praca');
  const mx0 = AV_W1 + t, mz0 = -9.6 + t, mx1 = -4.2 - t, mz1 = -5.6 - t;
  level._floor('tile', mx0, mz0, mx1, mz1, Y_IN, 0.4);
  level._place(P.makeShelf(level.mats, { w: 2.4, h: 2.1, d: 0.6 }), mx0 + 0.5, Y_IN,
    (mz0 + mz1) * 0.5, Math.PI / 2);
  level._place(P.makeShelf(level.mats, { w: 2.0, h: 1.9, d: 0.55 }), -6.4, Y_IN, mz0 + 0.5, 0);
  level._place(P.makeWorkbench(level.mats, { w: 2.0 }), -5.4, Y_IN, mz1 - 0.7, Math.PI);
  level._place(P.makeCrate(level.mats, { variant: 2, w: 0.7, h: 0.55, d: 0.7 }),
    -8.4, Y_IN, mz1 - 0.6, 0.4);
  level._place(P.makeCrate(level.mats, { variant: 1, w: 0.7, h: 0.55, d: 0.7 }),
    -8.4, Y_IN + 0.6, mz1 - 0.55, 0.9);
  level._place(P.makeHangingLamp(level.mats, { len: 0.6 }), -6.8, H_TERR - 0.3,
    (mz0 + mz1) * 0.5, 0);
  level._interiorDust(mx0, mz0, mx1, mz1, Y_IN, 0.8);
}

/**
 * O que se ve por cima da muralha: a vila continua descendo a encosta e o vale
 * fecha o quadro. Tudo em 'backdrop' na regiao 'fundo' - nao colide, nao projeta
 * sombra e sai em poucos bins.
 */
function buildBackdrop(level) {
  const b = level.batch;
  b.setRegion('fundo');
  const off = { solid: false, cast: false };

  // casario da encosta: caixas pequenas em terraco, descendo em degraus
  const rng = P.makeRng(2626);
  for (let i = 0; i < 46; i++) {
    const ring = 44 + Math.floor(i / 12) * 13;
    const a = (i * 0.53) + rng() * 0.22;
    const hx = Math.cos(a) * ring * P.rand(rng, 0.85, 1.2);
    const hz = Math.sin(a) * ring * P.rand(rng, 0.85, 1.2);
    if (Math.abs(hx) < 40 && Math.abs(hz) < 40) continue;
    const w = P.rand(rng, 5.5, 11.0);
    const d = P.rand(rng, 5.5, 10.0);
    const hh = P.rand(rng, 4.5, 9.5);
    const base = -1.2 - Math.floor(i / 12) * 1.4;
    b.setPhase(i * 17 + 5);
    level._slab('backdrop', hx - w * 0.5, base, hz - d * 0.5, hx + w * 0.5, base + hh,
      hz + d * 0.5, off);
    b.clearVariant();
    level._slab('concrete', hx - w * 0.5 - 0.25, base + hh, hz - d * 0.5 - 0.25,
      hx + w * 0.5 + 0.25, base + hh + 0.5, hz + d * 0.5 + 0.25, off);
  }

  /**
   * Massas grandes do vale. [x0, z0, x1, z1, altura, eixo da face voltada para o
   * mapa] - o eixo vem escrito porque deduzir pelo sinal poe a grade de janela
   * na face que ninguem ve.
   */
  const masses = [
    [-120, -70, -74, -14, 22, 'z'], [-108, 18, -66, 66, 18, 'z'],
    [66, -60, 112, -8, 20, 'z'], [74, 14, 118, 62, 26, 'z'],
    [-46, 74, 8, 116, 24, 'x'], [20, 78, 74, 118, 17, 'x'],
    [-30, -122, 22, -76, 21, 'x']
  ];
  for (let i = 0; i < masses.length; i++) {
    const m = masses[i];
    b.setPhase(i * 29 + 7);
    level._slab('backdrop', m[0], -2, m[1], m[2], m[4], m[3], off);
    b.clearVariant();
    level._slab('concrete', m[0] - 0.4, m[4], m[1] - 0.4, m[2] + 0.4, m[4] + 1.0, m[3] + 0.4, off);
    if (m[5] === 'x') {
      const south = m[3] < 0;
      level._blindWindows({ axis: 'x', face: south ? m[3] : m[1], sgn: south ? 1 : -1,
        u0: m[0] + 2, u1: m[2] - 2, y1: m[4] - 1.5, seed: i * 197 + 11, opts: off });
    } else {
      const west = m[0] > 0;
      level._blindWindows({ axis: 'z', face: west ? m[0] : m[2], sgn: west ? -1 : 1,
        u0: m[1] + 2, u1: m[3] - 2, y1: m[4] - 1.5, seed: i * 197 + 11, opts: off });
    }
  }

  // cristas do vale, bem atras: dao profundidade sem custar detalhe
  for (let i = 0; i < 5; i++) {
    const cx = -170 + i * 84;
    const cz = 150 + (i % 2) * 34;
    level._slab('backdrop', cx - 60, -6, cz - 30, cx + 60, 34 + (i % 3) * 12, cz + 30, off);
    level._slab('backdrop', -cz - 30, -6, cx - 60, -cz + 30, 30 + (i % 3) * 10, cx + 60, off);
  }
  // minarete distante: o eco da torre da praca no horizonte
  level._slab('backdrop', 58, -2, 74, 63, 30, 79, off);
  level._slab('concrete', 57, 30, 73, 64, 31.4, 80, off);
  level._slab('backdrop', 59.2, 31.4, 75.2, 61.8, 38, 77.8, off);

  b.setRegion('vila_centro');
}

// ---------------------------------------------------------------------------
// nascimentos e pontos de vista
// ---------------------------------------------------------------------------

/**
 * Todos os pontos ficam em rua, beco, patio ou praca - nunca dentro de casa
 * (32 das 34 tem porta fechada) e nunca a menos de 2 m de um muro atravessado
 * ou de uma escada. A altura sai de _groundY, menos nos tres pontos de telhado:
 * _groundY so conhece piso registrado por _floor, e laje de casa nao e piso.
 */
export function spawnPoints(level) {
  return {
    player: [
      level._spawn(-32.2, -32.2),                 // esquina sudoeste da muralha
      level._spawn(-4.0, -32.4),                  // rua da muralha, portao sul
      level._spawn(-29.6, -11.4),                 // travessa sul, ponta oeste
      level._spawn(-21.9, -18.0),                 // beco sudoeste
      level._spawn(-11.4, -27.0),                 // rua oeste, boca sul
      level._spawn(-6.5, -11.4),                  // travessa sul, sob a mercearia
      level._spawn(11.4, -25.5),                  // rua leste, boca sul
      level._spawn(-0.3, -19.0),                  // beco sul
      level._spawn(31.5, -27.0)                   // rua da muralha, leste-sul
    ],
    enemy: [
      level._spawn(-31.8, 32.2),                  // esquina noroeste da muralha
      level._spawn(-2.0, 32.4),                   // rua da muralha, norte
      level._spawn(31.8, 32.0),                   // esquina nordeste da muralha
      level._spawn(-6.0, 11.4),                   // travessa norte, centro
      level._spawn(23.5, 11.4),                   // travessa norte, leste
      level._spawn(11.4, 24.0),                   // rua leste, boca norte
      level._spawn(-24.0, 21.3),                  // beco noroeste
      level._spawn(32.2, -5.0),                   // rua da muralha, leste
      level._spawn(-2.0, -6.5),                   // praca, entre a fonte e a mercearia
      level._spawn(-23.4, 26.0, H_CASA + 0.08),   // telhado noroeste
      level._spawn(17.8, 29.4, H_TERR + 0.08)     // terraco nordeste
    ]
  };
}

/** Ver a nota de `pos`/`position` em porto.js: e o mesmo array, dois nomes. */
function vp(out, name, fx, fy, fz, tx, ty, tz) {
  const dx = tx - fx;
  const dy = ty - fy;
  const dz = tz - fz;
  const yaw = Math.round((Math.atan2(-dx, -dz) * 180) / Math.PI * 10) / 10;
  const pitch = Math.round((Math.atan2(dy, Math.hypot(dx, dz)) * 180) / Math.PI * 10) / 10;
  const pos = [fx, fy, fz];
  out.push({ name, pos, position: pos, yaw, pitch });
}

export function viewpoints(level) {
  const eye = 1.65;
  const out = [];
  vp(out, 'vila_rua_oeste', -11.4, Y_ST + eye, -30.0, -11.4, 2.4, 6.0);
  vp(out, 'vila_travessa_sul', -30.0, Y_ST + eye, -11.4, 20.0, 2.0, -11.4);
  vp(out, 'vila_beco_sudoeste', -21.9, Y_ST + eye, -29.0, -21.9, 2.6, -14.0);
  vp(out, 'vila_praca', -7.4, Y_SQ + eye, -7.0, 6.6, 6.0, 7.0);
  vp(out, 'vila_arcada', -1.0, ARC_TOP + eye, 8.4, -2.0, 0.8, -8.0);
  vp(out, 'vila_torre', 0.0, Y_SQ + eye, -4.0, 7.0, 9.4, 6.4);
  vp(out, 'vila_telhado_sul', -26.5, H_CASA + eye, -24.0, -6.0, 4.0, -26.0);
  vp(out, 'vila_escada_rua', -12.6, Y_ST + eye, -12.0, -14.0, 4.4, -5.0);
  vp(out, 'vila_muralha', -32.0, Y_ST + eye, -24.0, -32.0, 3.2, 12.0);
  vp(out, 'vila_oficina', 8.6, Y_IN + eye, -19.6, 9.6, 1.2, -17.0);
  vp(out, 'vila_plano_geral', -33.0, 17.0, -33.0, 2.0, 2.0, 6.0);
  vp(out, 'vila_plano_geral_norte', 33.0, 16.0, 33.0, -4.0, 2.0, -6.0);
  vp(out, 'vila_parede_close', -10.6, 1.5, -4.0, -9.6, 1.45, -4.2);
  return out;
}
